import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheck,
  FaSearch,
  FaSave,
  FaTimes,
  FaPlus,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Loader/Loader';
import './SubjectSelection.css';

const CUSTOM_OPTION = '__custom__';
const DEFAULT_CUSTOM_CATEGORY = 'Custom';
const DEFAULT_CUSTOM_SUBCATEGORY = 'General';
const normalize = (value) => (value || '').trim().toLowerCase();
const getSubjectKey = ({ category, subcategory, subject }) =>
  `${normalize(category)}|${normalize(subcategory)}|${normalize(subject)}`;

const SubjectSelection = ({ selectedOnly = false, pageTitle = 'Choose Your Subjects' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const selectedOnlyView = Boolean(selectedOnly || location.state?.selectedOnly);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState(location.state?.searchQuery || '');
  const [showAllSubjects, setShowAllSubjects] = useState(!selectedOnlyView);

  const [customSubjectName, setCustomSubjectName] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [customSubcategory, setCustomSubcategory] = useState('');
  const [customCategoryText, setCustomCategoryText] = useState('');
  const [customSubcategoryText, setCustomSubcategoryText] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [catRes, selRes] = await Promise.all([
        axios.get(api.getExamCategories, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(api.getUserSubjectSelection, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (catRes.data.success) {
        setCategories(catRes.data.data || {});
      }

      if (selRes.data.success && selRes.data.data?.subjects) {
        setSelectedSubjects(selRes.data.data.subjects);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dbTopics = useMemo(() => {
    if (!categories) return [];

    const allTopics = [];
    Object.entries(categories).forEach(([categoryName, subcategories]) => {
      Object.entries(subcategories || {}).forEach(([subcategoryName, topics]) => {
        (topics || []).forEach((topic) => {
          allTopics.push({
            subject: topic,
            category: categoryName,
            subcategory: subcategoryName,
          });
        });
      });
    });

    return allTopics.sort((a, b) => a.subject.localeCompare(b.subject));
  }, [categories]);

  const selectedKeySet = useMemo(() => {
    return new Set(selectedSubjects.map((item) => getSubjectKey(item)));
  }, [selectedSubjects]);

  const allTopics = useMemo(() => {
    const merged = new Map();

    dbTopics.forEach((item) => {
      merged.set(getSubjectKey(item), item);
    });

    selectedSubjects.forEach((item) => {
      const key = getSubjectKey(item);
      if (!merged.has(key)) {
        merged.set(key, { ...item, isCustom: true });
      }
    });

    return Array.from(merged.values()).sort((a, b) => a.subject.localeCompare(b.subject));
  }, [dbTopics, selectedSubjects]);

  const visibleTopics = useMemo(() => {
    const sourceTopics = showAllSubjects ? allTopics : selectedSubjects;

    if (!searchQuery.trim()) return sourceTopics;
    const query = normalize(searchQuery);

    return sourceTopics.filter((item) =>
      normalize(item.subject).includes(query) ||
      normalize(item.category).includes(query) ||
      normalize(item.subcategory).includes(query),
    );
  }, [allTopics, selectedSubjects, searchQuery, showAllSubjects]);

  const isSubjectSelected = (item) => selectedKeySet.has(getSubjectKey(item));
  const useChipMode = selectedSubjects.length > 3;

  const toggleSubject = (item) => {
    const key = getSubjectKey(item);

    if (selectedKeySet.has(key)) {
      setSelectedSubjects((prev) => prev.filter((s) => getSubjectKey(s) !== key));
      return;
    }

    setSelectedSubjects((prev) => [
      ...prev,
      {
        subject: item.subject.trim(),
        category: item.category.trim(),
        subcategory: item.subcategory.trim(),
        addedAt: new Date().toISOString(),
      },
    ]);
  };

  const removeSubjectWithConfirm = async (item) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Remove Subject?',
      html: `Are you sure you want to remove <strong>${item.subject}</strong> from your selected subjects?<br><br><small>Click Save after removing to update My Subjects.</small>`,
      showCancelButton: true,
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#2563eb',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    const key = getSubjectKey(item);
    setSelectedSubjects((prev) => prev.filter((s) => getSubjectKey(s) !== key));
    toast.warning(`"${item.subject}" removed. Click Save to update your subjects.`);
  };

  const categoryOptions = useMemo(() => Object.keys(categories || {}), [categories]);

  const subcategoryOptions = useMemo(() => {
    if (!customCategory || customCategory === CUSTOM_OPTION || !categories) return [];
    return Object.keys(categories[customCategory] || {});
  }, [customCategory, categories]);

  const resetCustomForm = () => {
    setCustomSubjectName('');
    setCustomCategory('');
    setCustomSubcategory('');
    setCustomCategoryText('');
    setCustomSubcategoryText('');
  };

  const addCustomSubject = () => {
    const subject = customSubjectName.trim();
    const category =
      customCategory === CUSTOM_OPTION ? customCategoryText.trim() : customCategory.trim();
    const subcategory =
      customSubcategory === CUSTOM_OPTION ? customSubcategoryText.trim() : customSubcategory.trim();

    if (!subject) {
      toast.warning('Please enter a subject/topic name');
      return;
    }

    const newEntry = {
      subject,
      category: category || DEFAULT_CUSTOM_CATEGORY,
      subcategory: subcategory || DEFAULT_CUSTOM_SUBCATEGORY,
      isCustom: true,
    };
    const newKey = getSubjectKey(newEntry);

    if (selectedKeySet.has(newKey)) {
      toast.info('This subject is already selected');
      return;
    }

    setSelectedSubjects((prev) => [
      ...prev,
      {
        ...newEntry,
        addedAt: new Date().toISOString(),
      },
    ]);

    toast.success('Custom subject added');
    resetCustomForm();
  };

  const saveSelection = async () => {
    setSaving(true);
    try {
      await axios.post(
        api.createSubjectSelection,
        { subjects: selectedSubjects },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Subjects saved successfully');
    } catch (error) {
      console.error('Error saving subjects:', error);
      toast.error('Failed to save subjects');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader message="Loading subjects..." />;

  return (
    <div
      className={`subject-selection ${selectedOnlyView ? 'selected-subjects-page' : ''} ${
        selectedOnlyView && showAllSubjects ? 'show-all-mode' : ''
      }`}
    >
      <header className="subject-header">
        <button className="subject-back-btn" onClick={() => navigate('/university/dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <div className="subject-header-title-wrap">
          <h1 className={selectedOnlyView ? 'selected-subjects-title' : ''}>{pageTitle}</h1>
          {selectedOnlyView && <span className="subject-page-badge">Personal Track</span>}
        </div>
        <div className="subject-header-spacer"></div>
      </header>

      <section className="subject-controls-wrap">
        <div className="subject-search-container">
          <FaSearch className="subject-search-icon" />
          <input
            type="text"
            placeholder="Search topic, category, subcategory..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="subject-search-input"
          />
          {searchQuery && (
            <button className="subject-search-clear" onClick={() => setSearchQuery('')}>
              <FaTimes />
            </button>
          )}
        </div>

        <div className="subject-selected-count">
          <FaCheck /> {selectedSubjects.length} selected
        </div>
        <button
          className={`subject-mode-toggle ${selectedOnlyView ? 'selected-page-toggle' : ''}`}
          onClick={() => setShowAllSubjects((prev) => !prev)}
        >
          {showAllSubjects
            ? (selectedOnlyView ? 'Back to My Selected List' : 'Show Only Selected')
            : 'Add More Subjects'}
        </button>
      </section>

      {selectedOnlyView && (
        <section className="selected-track-summary">
          <div className="selected-track-summary-card">
            <span className="selected-track-summary-number">{selectedSubjects.length}</span>
            <span className="selected-track-summary-label">Subjects In My Track</span>
          </div>
          <div className="selected-track-summary-note">
            {showAllSubjects
              ? 'Browse all subjects and add more to your track.'
              : 'You are in focused mode: only your selected subjects are shown.'}
          </div>
        </section>
      )}

      <section className="subject-content">
        <div className="subject-translator-card">
          <div className="subject-section-head">
            {/* <h2>All Topics from Database</h2> */}
            <h2>{showAllSubjects ? 'Choose the subjects for Examination' : 'Your Selected Subjects'}</h2>
            <p>
              {showAllSubjects
                ? `${visibleTopics.length} subjects available`
                : `${visibleTopics.length} selected subjects shown`}
            </p>
          </div>

          <div className="subject-language-grid">
            {visibleTopics.map((item) => {
              const selected = isSubjectSelected(item);
              return (
                <button
                  key={getSubjectKey(item)}
                  className={`subject-language-row ${selected ? 'selected' : ''}`}
                  onClick={() => toggleSubject(item)}
                >
                  <div className="subject-language-main">
                    <span className="subject-language-name">{item.subject}</span>
                    <span className="subject-language-meta">
                      {item.category} / {item.subcategory}
                    </span>
                  </div>
                  <span className="subject-language-state">{selected ? <FaCheck /> : '+'}</span>
                </button>
              );
            })}

            {visibleTopics.length === 0 && (
              <p className="subject-empty-state">
                {showAllSubjects
                  ? 'No topics matched your search.'
                  : 'No selected subjects found. Click Add More Subjects to choose from all subjects.'}
              </p>
            )}
          </div>
        </div>

        <div className="subject-side-column">
          <div className="subject-selected-card">
            <div className="subject-section-head">
              <h2>{selectedOnlyView ? 'My Current Subjects' : 'Selected Subjects'}</h2>
              <p>
                {selectedOnlyView
                  ? 'Manage your personal subject track and update it anytime.'
                  : 'Select any number of subjects based on your exam preference.'}
              </p>
            </div>

            <div className={`subject-selected-list ${useChipMode ? 'chip-mode' : ''}`}>
              {selectedSubjects.map((item) => (
                <div
                  key={getSubjectKey(item)}
                  className={`subject-selected-item ${useChipMode ? 'chip-mode' : ''}`}
                >
                  <div className="subject-selected-main">
                    <span className="subject-selected-name">{item.subject}</span>
                    {!useChipMode && (
                      <span className="subject-selected-meta">
                        {item.category} / {item.subcategory}
                      </span>
                    )}
                  </div>
                  <button
                    className="subject-selected-remove"
                    onClick={() => removeSubjectWithConfirm(item)}
                    aria-label={`Remove ${item.subject}`}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}

              {selectedSubjects.length === 0 && (
                <p className="subject-empty-state">
                  No subjects selected yet. Pick at least one subject to start.
                </p>
              )}
            </div>

            <button
              className="subject-save-btn subject-save-btn-card"
              onClick={saveSelection}
              disabled={saving}
            >
              <FaSave /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div className="subject-custom-card">
            <div className="subject-section-head">
              <h2>Add Your Own Subject</h2>
              <p>Create custom topics if not listed above. Category and subcategory are optional.</p>
            </div>

            <div className="subject-custom-form">
              <input
                type="text"
                placeholder="Subject or Topic name"
                value={customSubjectName}
                onChange={(e) => setCustomSubjectName(e.target.value)}
                className="subject-custom-input"
              />

              <select
                value={customCategory}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomCategory(value);
                  setCustomSubcategory('');
                  if (value !== CUSTOM_OPTION) {
                    setCustomCategoryText('');
                  }
                }}
                className="subject-custom-select"
              >
                <option value="">Choose category (optional)</option>
                {categoryOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
                <option value={CUSTOM_OPTION}>Other (type category)</option>
              </select>

              {customCategory === CUSTOM_OPTION && (
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={customCategoryText}
                  onChange={(e) => setCustomCategoryText(e.target.value)}
                  className="subject-custom-input"
                />
              )}

              <select
                value={customSubcategory}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomSubcategory(value);
                  if (value !== CUSTOM_OPTION) {
                    setCustomSubcategoryText('');
                  }
                }}
                className="subject-custom-select"
              >
                <option value="">Choose subcategory (optional)</option>
                {subcategoryOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
                <option value={CUSTOM_OPTION}>Other (type subcategory)</option>
              </select>

              {customSubcategory === CUSTOM_OPTION && (
                <input
                  type="text"
                  placeholder="Enter subcategory name"
                  value={customSubcategoryText}
                  onChange={(e) => setCustomSubcategoryText(e.target.value)}
                  className="subject-custom-input"
                />
              )}

              <button className="subject-custom-add-btn" onClick={addCustomSubject}>
                <FaPlus /> Add Subject
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubjectSelection;
